const SKIP_SHEET_NAMES = ['הסבר על יצירת האקסל'];

const BUILDINGS_COLUMN_ALIASES = {
  id: ['מזהה בניין', 'מספר בניין', 'מספר', 'number', 'Number'],
  name: ['שם בניין', 'תאור', 'בניין', 'Name', 'Building', 'name'],
  numAprts: ['מספר דירות', 'numAprts', 'NumAprts'],
  numFlrs: ['מספר קומות', 'numFlrs', 'NumFlrs'],
};

const TENANTS_COLUMN_ALIASES = {
  id: ['תז', 'ID', 'id', 'IdNumber'],
  phone: ['טלפון', 'טלפון 1', 'Phone', 'Phone1', 'phone1'],
  phone2: ['טלפון 2', 'Phone2', 'phone2'],
  email: ['מייל', 'אימייל', 'Email', 'email'],
  note: ['הערות', 'Notes', 'note'],
  building: ['בניין', 'Building', 'building'],
  floor: ['קומה', 'Floor', 'floor'],
  apartment: ['דירה', 'Apartment', 'apartment'],
  appType: ['טיפוס דירה', 'טיפוס', 'ApartmentType', 'appType'],
  alwEmail: ['אישור שליחה במייל', 'מורשה שליחה במייל', 'AlwEmail'],
  alwSms: ['אישור שליחה בהודעה', 'מורשה שליחת הודעה', 'U_AlwSms', 'AlwSms'],
  alwWapp: ['אישור שליחה בוואצאפ', 'מורשה שליחה בוואצאפ', 'U_AlwWApp', 'AlwWApp'],
};

const BOQ_COLUMN_ALIASES = {
  apartment: ['דירה', 'aprt', 'Aprt', 'Apartment'],
  room: ['שם חדר', 'חדר', 'Room', 'room'],
  code: ['קוד פריט', 'קוד מגוון', 'קוד', 'Code', 'code'],
  name: ['תיאור פריט', 'מגוון', 'שם פריט', 'פריט', 'Name', 'Item', 'name'],
  unit: ['יחידה', 'י.מידה', 'Unit', 'unit'],
  quantity: ['כמות', 'כמות לפני העמסה', 'Quantity', 'quantity'],
  wstgPrcnt: ['אחוז פחת', 'wstgPrcnt', 'WstgPrcnt'],
  bufferQty: ['כמות לבדק', 'bufferQty', 'BufferQty'],
  totalQty: ['סך כמות', 'סך הכל'],
  shipDate: ['תאריך אספקה משוער', 'ת.אספקה משוער', 'תאריך אספקה', 'shipDate'],
  building: ['בניין', 'Building', 'building'],
};

export const EMPTY_VALIDATION = {
  errors: [],
  warnings: [],
  buildingRows: {},
  tenantRows: {},
  boqRows: {},
  errorCount: 0,
  warningCount: 0,
  hasBlockingErrors: false,
};

function normalizeColumns(columns) {
  return (columns || []).map((c) => String(c).trim());
}

function hasAnyColumn(columns, aliases) {
  const normalized = normalizeColumns(columns);
  return aliases.some((alias) => normalized.includes(alias));
}

function getMissingLogicalColumns(columns, aliasMap, labels) {
  const missing = [];
  Object.entries(aliasMap).forEach(([key, aliases]) => {
    if (!hasAnyColumn(columns, aliases)) {
      missing.push(labels[key] || key);
    }
  });
  return missing;
}

function getSheetColumnNames(workbook, sheetIndex) {
  const sheetName = workbook.SheetNames[sheetIndex];
  if (!sheetName) return [];
  const sheet = workbook.Sheets[sheetName];
  if (!sheet || !sheet['!ref']) return [];

  const range = sheet['!ref'];
  const headers = [];
  const decodeRange = (ref) => {
    const match = ref.match(/([A-Z]+)(\d+):([A-Z]+)(\d+)/i);
    if (!match) return null;
    return { startCol: match[1], startRow: parseInt(match[2], 10) };
  };

  const parsed = decodeRange(range);
  if (!parsed) return [];

  const colToNum = (col) => {
    let num = 0;
    for (let i = 0; i < col.length; i += 1) {
      num = num * 26 + (col.charCodeAt(i) - 64);
    }
    return num;
  };

  const startColNum = colToNum(parsed.startCol);
  const maxCol = 40;
  for (let c = startColNum; c < startColNum + maxCol; c += 1) {
    let colLetter = '';
    let n = c;
    while (n > 0) {
      const rem = (n - 1) % 26;
      colLetter = String.fromCharCode(65 + rem) + colLetter;
      n = Math.floor((n - 1) / 26);
    }
    const cellAddress = `${colLetter}${parsed.startRow}`;
    const cell = sheet[cellAddress];
    if (!cell || cell.v === undefined || cell.v === null || String(cell.v).trim() === '') {
      if (headers.length > 0) break;
      continue;
    }
    headers.push(String(cell.v).trim());
  }

  return headers;
}

function addRowError(map, key, message) {
  if (!map[key]) map[key] = [];
  if (!map[key].includes(message)) map[key].push(message);
}

function isPositiveInt(value) {
  const str = String(value ?? '').trim();
  if (!str) return false;
  return /^\d+$/.test(str) && Number(str) >= 0;
}

function normalizeUnit(unit) {
  return String(unit ?? '').replace(/"/g, '').trim();
}

function isWholeNumber(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return false;
  return Math.abs(num - Math.round(num)) < 1e-9;
}

function parseShipDate(value) {
  if (value === null || value === undefined || value === '') return null;

  try {
    let date;

    if (value instanceof Date) {
      date = value;
    } else if (typeof value === 'string') {
      const dmyMatch = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (dmyMatch) {
        const [, day, month, year] = dmyMatch;
        date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
      } else {
        const sqlDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (sqlDateMatch) {
          const [, year, month, day] = sqlDateMatch;
          date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
        } else {
          date = new Date(value);
        }
      }
    } else if (typeof value === 'number') {
      if (value > 25569 && value < 100000) {
        date = new Date(Math.round((value - 25569) * 86400 * 1000));
      } else {
        date = new Date(value);
      }
    } else {
      return null;
    }

    if (isNaN(date.getTime())) return null;
    return date;
  } catch {
    return null;
  }
}

function isFutureShipDate(value) {
  const date = parseShipDate(value);
  if (!date) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const shipDay = new Date(date);
  shipDay.setHours(0, 0, 0, 0);
  return shipDay >= today;
}

export function validateExcelStructure(workbook) {
  const result = { ...EMPTY_VALIDATION, buildingRows: {}, tenantRows: {}, boqRows: {} };
  const sheetNames = workbook?.SheetNames || [];

  if (sheetNames.length < 3) {
    result.errors.push('הקובץ חייב להכיל לפחות 3 לשוניות: בניינים, רשימת דיירים, ופירוט לבניינים');
    result.hasBlockingErrors = true;
    result.errorCount = result.errors.length;
    return result;
  }

  const buildingsCols = getSheetColumnNames(workbook, 0);
  const missingBuildings = getMissingLogicalColumns(buildingsCols, BUILDINGS_COLUMN_ALIASES, {
    id: 'מזהה בניין',
    name: 'שם בניין',
    numAprts: 'מספר דירות',
    numFlrs: 'מספר קומות',
  });
  if (missingBuildings.length > 0) {
    result.errors.push(`בלשונית הראשונה חסרות עמודות: ${missingBuildings.join(', ')}`);
  }

  const tenantsCols = getSheetColumnNames(workbook, 1);
  const missingTenants = getMissingLogicalColumns(tenantsCols, TENANTS_COLUMN_ALIASES, {
    id: 'תז',
    phone: 'טלפון',
    building: 'בניין',
    floor: 'קומה',
    apartment: 'דירה',
    appType: 'טיפוס דירה',
  });
  if (missingTenants.length > 0) {
    result.errors.push(`בלשונית השנייה (דיירים) חסרות עמודות: ${missingTenants.join(', ')}`);
  }

  for (let i = 2; i < sheetNames.length; i += 1) {
    const sheetName = sheetNames[i];
    if (SKIP_SHEET_NAMES.includes(sheetName)) continue;

    const cols = getSheetColumnNames(workbook, i);
    if (cols.length === 0) continue;

    const missingBoq = getMissingLogicalColumns(cols, BOQ_COLUMN_ALIASES, {
      apartment: 'דירה',
      room: 'שם חדר',
      code: 'קוד פריט',
      name: 'תיאור פריט',
      unit: 'יחידה',
      quantity: 'כמות',
      wstgPrcnt: 'אחוז פחת',
      bufferQty: 'כמות לבדק',
      shipDate: 'תאריך אספקה משוער',
    });
    if (missingBoq.length > 0) {
      result.errors.push(`בלשונית "${sheetName}" חסרות עמודות: ${missingBoq.join(', ')}`);
    }
  }

  result.hasBlockingErrors = result.errors.length > 0;
  result.errorCount = result.errors.length;
  return result;
}

export function isPieceUnit(unit) {
  return normalizeUnit(unit).toLowerCase() === 'יחידה';
}

export function validateBoqData({ buildings = [], tenants = [], boqItems = {}, migvan = [] }) {
  const result = {
    errors: [],
    warnings: [],
    buildingRows: {},
    tenantRows: {},
    boqRows: {},
    errorCount: 0,
    warningCount: 0,
    hasBlockingErrors: false,
  };

  const migvanByCode = new Map(
    (migvan || []).map((m) => [String(m.code || '').trim(), m])
  );
  const migvanCodes = new Set((migvan || []).map((m) => String(m.code || '').trim()).filter(Boolean));
  const fileItemCodes = new Set();

  if (buildings.length === 0) {
    result.errors.push('חייב להיות לפחות בניין אחד');
  }

  buildings.forEach((building, index) => {
    const rowKey = building.id;
    const rowLabel = `בניין ${index + 1}`;

    if (!String(building.name || '').trim()) {
      addRowError(result.buildingRows, rowKey, 'תאור בניין הוא שדה חובה');
      result.errors.push(`${rowLabel}: תאור בניין הוא שדה חובה`);
    }
    if (!String(building.number || '').trim()) {
      addRowError(result.buildingRows, rowKey, 'מספר בניין הוא שדה חובה');
      result.errors.push(`${rowLabel}: מספר בניין הוא שדה חובה`);
    }
    if (building.numFlrs === undefined || building.numFlrs === null || building.numFlrs === '') {
      addRowError(result.buildingRows, rowKey, 'מספר קומות הוא שדה חובה');
      result.errors.push(`${rowLabel}: מספר קומות הוא שדה חובה`);
    }
    if (building.numAprts === undefined || building.numAprts === null || building.numAprts === '') {
      addRowError(result.buildingRows, rowKey, 'מספר דירות הוא שדה חובה');
      result.errors.push(`${rowLabel}: מספר דירות הוא שדה חובה`);
    }
  });

  const totalApartments = buildings.reduce((sum, b) => sum + (Number(b.numAprts) || 0), 0);
  if (tenants.length > 0 && totalApartments > 0 && tenants.length !== totalApartments) {
    result.errors.push(`מספר דיירים (${tenants.length}) חייב להיות שווה לסך הדירות (${totalApartments})`);
  }

  const buildingNumbers = new Set(
    buildings.map((b) => String(b.number || '').trim()).filter(Boolean)
  );
  const buildingFloorsByNumber = new Map(
    buildings.map((b) => [String(b.number || '').trim(), Math.floor(Number(b.numFlrs)) || 0])
  );

  tenants.forEach((tenant, index) => {
    const rowKey = tenant.tenantId;
    const rowLabel = `דייר ${index + 1}`;

    if (!String(tenant.name || '').trim()) {
      addRowError(result.tenantRows, rowKey, 'שם פרטי הוא שדה חובה');
      result.errors.push(`${rowLabel}: שם פרטי הוא שדה חובה`);
    }
    if (!String(tenant.family || '').trim()) {
      addRowError(result.tenantRows, rowKey, 'משפחה היא שדה חובה');
      result.errors.push(`${rowLabel}: משפחה היא שדה חובה`);
    }
    if (!String(tenant.id || '').trim()) {
      addRowError(result.tenantRows, rowKey, 'תז היא שדה חובה');
      result.errors.push(`${rowLabel}: תז היא שדה חובה`);
    }
    if (!String(tenant.phone1 || '').trim()) {
      addRowError(result.tenantRows, rowKey, 'טלפון 1 הוא שדה חובה');
      result.errors.push(`${rowLabel}: טלפון 1 הוא שדה חובה`);
    }

    const buildingVal = String(tenant.building || '').trim();
    if (!buildingVal) {
      addRowError(result.tenantRows, rowKey, 'בניין הוא שדה חובה');
      result.errors.push(`${rowLabel}: בניין הוא שדה חובה`);
    } else if (!isPositiveInt(buildingVal)) {
      addRowError(result.tenantRows, rowKey, 'בניין חייב להיות מספר');
      result.errors.push(`${rowLabel}: בניין ודירה חייבים להיות מספריים`);
    } else if (!buildingNumbers.has(buildingVal)) {
      addRowError(result.tenantRows, rowKey, `בניין "${buildingVal}" לא קיים ברשימת הבניינים`);
      result.errors.push(`${rowLabel}: בניין "${buildingVal}" לא קיים ברשימת הבניינים`);
    }

    const floorVal = String(tenant.floor || '').trim();
    if (!floorVal) {
      addRowError(result.tenantRows, rowKey, 'קומה היא שדה חובה');
      result.errors.push(`${rowLabel}: קומה היא שדה חובה`);
    } else if (!isPositiveInt(floorVal)) {
      addRowError(result.tenantRows, rowKey, 'קומה חייבת להיות מספר');
      result.errors.push(`${rowLabel}: קומה חייבת להיות מספר חיובי`);
    } else if (buildingNumbers.has(buildingVal)) {
      const maxFloors = buildingFloorsByNumber.get(buildingVal) || 0;
      if (maxFloors > 0 && Number(floorVal) > maxFloors) {
        addRowError(
          result.tenantRows,
          rowKey,
          `קומה ${floorVal} חורגת ממספר הקומות בבניין ${buildingVal} (${maxFloors})`
        );
        result.errors.push(
          `${rowLabel}: קומה ${floorVal} חורגת ממספר הקומות בבניין ${buildingVal} (מקסימום ${maxFloors})`
        );
      }
    }

    const apartmentVal = String(tenant.apartment || '').trim();
    if (!apartmentVal) {
      addRowError(result.tenantRows, rowKey, 'דירה היא שדה חובה');
      result.errors.push(`${rowLabel}: דירה היא שדה חובה`);
    } else if (!isPositiveInt(apartmentVal)) {
      addRowError(result.tenantRows, rowKey, 'דירה חייבת להיות מספר');
      result.errors.push(`${rowLabel}: דירה חייבת להיות מספר חיובי`);
    }

    if (!String(tenant.appType || '').trim()) {
      addRowError(result.tenantRows, rowKey, 'טיפוס הוא שדה חובה');
      result.errors.push(`${rowLabel}: טיפוס הוא שדה חובה`);
    }
  });

  const duplicateKeys = new Set();
  const seenItemKeys = new Set();

  buildings.forEach((building) => {
    const buildingNumber = String(building.number || '').trim();
    const buildingId = building.id;
    const key = String(buildingId);
    const items = boqItems[key] || [];
    const buildingTenants = tenants.filter((t) => String(t.building).trim() === buildingNumber);

    buildingTenants.forEach((tenant) => {
      const aprt = String(tenant.apartment || '').trim();
      const itemsForApartment = items.filter((item) => String(item.aprt || '').trim() === aprt);

      if (itemsForApartment.length === 0) {
        result.errors.push(`כתב כמויות: חסר פריט עבור בניין ${buildingNumber}, דירה ${aprt}`);
      }
    });

    items.forEach((item, itemIndex) => {
      const rowKey = `${buildingId}:${item.id}`;
      const rowLabel = `כתב כמויות בניין ${buildingNumber}, שורה ${itemIndex + 1}`;
      const code = String(item.code || '').trim();
      const aprt = String(item.aprt || '').trim();
      const room = String(item.room || '').trim();
      const quantity = Number(item.quantity) || 0;
      const rowBuilding = String(item.בניין || item.building || buildingNumber).trim().replace(/\s/g, '');
      const expectedBuilding = String(buildingNumber).trim().replace(/\s/g, '');

      if (rowBuilding && expectedBuilding && rowBuilding !== expectedBuilding) {
        addRowError(result.boqRows, rowKey, `בניין ${rowBuilding} לא תואם ללשונית בניין ${expectedBuilding}`);
        result.errors.push(`${rowLabel}: בניין ${rowBuilding} לא בלשונית המתאימה`);
      }

      if (!aprt) {
        addRowError(result.boqRows, rowKey, 'דירה היא שדה חובה');
        result.errors.push(`${rowLabel}: דירה היא שדה חובה`);
      }

      if (!String(item.name || '').trim() && !code) {
        addRowError(result.boqRows, rowKey, 'מגוון / קוד פריט הוא שדה חובה');
        result.errors.push(`${rowLabel}: מגוון הוא שדה חובה`);
      }

      if (quantity <= 0) {
        addRowError(result.boqRows, rowKey, 'כמות חייבת להיות גדולה מ-0');
        result.errors.push(`${rowLabel}: כמות חייבת להיות מעל 0`);
      } else if (isPieceUnit(item.unit) && !isWholeNumber(quantity)) {
        addRowError(result.boqRows, rowKey, 'כמות לפני העמסה חייבת להיות מספר שלם עבור יחידת מידה "יחידה"');
        result.errors.push(`${rowLabel}: כמות לפני העמסה חייבת להיות מספר שלם עבור יחידת מידה "יחידה"`);
      }

      if (code) {
        fileItemCodes.add(code);
        if (migvanCodes.size > 0 && !migvanCodes.has(code)) {
          addRowError(result.boqRows, rowKey, `קוד פריט ${code} אינו קיים במיגוון`);
          result.errors.push(`${rowLabel}: קוד פריט ${code} אינו קיים במיגוון`);
        }

        const migvanItem = migvanByCode.get(code);
        if (migvanItem) {
          const contractUnit = normalizeUnit(migvanItem.unit);
          const fileUnit = normalizeUnit(item.unit);
          if (contractUnit && fileUnit && contractUnit.toLowerCase() !== fileUnit.toLowerCase()) {
            addRowError(result.boqRows, rowKey, `יחידת מידה לא תואמת לחוזה (${fileUnit} ≠ ${contractUnit})`);
            result.errors.push(`${rowLabel}: יחידת מידה של פריט ${code} אינה תואמת לחוזה`);
          }
        }
      }

      if (buildingNumber && aprt && room && code) {
        const dupKey = `${buildingNumber}|${aprt}|${room}|${code}`;
        if (seenItemKeys.has(dupKey)) {
          if (!duplicateKeys.has(dupKey)) {
            duplicateKeys.add(dupKey);
            result.errors.push(`קיים פריט כפול: בניין ${buildingNumber}, דירה ${aprt}, חדר ${room}, פריט ${code}`);
          }
          addRowError(result.boqRows, rowKey, 'פריט כפול לאותו בניין/דירה/חדר');
        } else {
          seenItemKeys.add(dupKey);
        }
      }

      const shipDateValue = item.shipDate;
      if (shipDateValue) {
        if (!isFutureShipDate(shipDateValue)) {
          addRowError(result.boqRows, rowKey, 'ת.אספקה משוער חייב להיות עתידי');
          result.errors.push(`${rowLabel}: ת.אספקה משוער חייב להיות עתידי`);
        }
      }
    });
  });

  if (migvanCodes.size > 0) {
    migvanCodes.forEach((code) => {
      if (!fileItemCodes.has(code)) {
        result.warnings.push(`לפריט ${code} אין מופע בקובץ`);
      }
    });
  }

  result.errorCount = result.errors.length;
  result.warningCount = result.warnings.length;
  result.hasBlockingErrors = result.errors.length > 0;
  return result;
}

export function mergeValidations(...validations) {
  const merged = { ...EMPTY_VALIDATION, buildingRows: {}, tenantRows: {}, boqRows: {} };

  validations.forEach((v) => {
    if (!v) return;
    merged.errors.push(...(v.errors || []));
    merged.warnings.push(...(v.warnings || []));
    Object.entries(v.buildingRows || {}).forEach(([k, msgs]) => {
      msgs.forEach((m) => addRowError(merged.buildingRows, k, m));
    });
    Object.entries(v.tenantRows || {}).forEach(([k, msgs]) => {
      msgs.forEach((m) => addRowError(merged.tenantRows, k, m));
    });
    Object.entries(v.boqRows || {}).forEach(([k, msgs]) => {
      msgs.forEach((m) => addRowError(merged.boqRows, k, m));
    });
  });

  merged.errorCount = merged.errors.length;
  merged.warningCount = merged.warnings.length;
  merged.hasBlockingErrors = merged.errors.length > 0;
  return merged;
}
